import { intersection, merge } from 'lodash';
import { useMemo, useState } from 'react';
import { useBlockUnmountContext } from '../../../../contexts/BlockUnmountContext/BlockUnmountContext';
import { useMixpanelContext } from '../../../../contexts/MixpanelContext/MixpanelContext';
import { MixpanelEvents } from '../../../../contexts/MixpanelContext/MixpanelEvents';
import { PatientInfo } from '../../../../contexts/PatientInfoContext/PatientInfoContext';
import { usePatientMedicalOrganizationContext } from '../../../../contexts/PatientMedicalOrganizationContext/PatientMedicalOrganizationContext';
import { useUpdate, useUpdateListener } from '../../../../contexts/UpdateContext/UpdateContext';
import { ApiRequestHelper } from '../../../../helpers/ApiRequest';
import {
  EnrollmentActionEnum,
  usePatientUpdateEnrollment,
  usePatientUpdateHealthCondition,
  usePatientUpdatePatientComplexity
} from '../../../../uc-api-sdk';
import { EnrollmentWrapperComponent } from '../../component/EnrollmentWrapperComponent/EnrollmentWrapperComponent';
import {
  ProgramEnrollmentFormComponent, ProgramEnrollmentFormComponentProps, SubmitValue
} from '../../component/ProgramEnrollmentFormComponent/ProgramEnrollmentFormComponent';
import { EnrollmentReq } from '../../type';

export interface ProgramEnrollmentFormContainerProps {
  patientInfo: PatientInfo;
  onSubmit?: (value: SubmitValue) => void;
  onClose?: () => void;
}

export const ProgramEnrollmentFormContainer = ({
  patientInfo,
  onSubmit,
  onClose,
}: ProgramEnrollmentFormContainerProps) => {
  const { send: sendMixpanel } = useMixpanelContext();
  const [currValue, setCurrValue] = useState<SubmitValue>();
  const programEnrollmentUpdate = usePatientUpdateEnrollment({});
  const healthConditionUpdateInfo = usePatientUpdateHealthCondition({});
  const patientComplexityUpdateInfo = usePatientUpdatePatientComplexity({});
  const { isLoading } = programEnrollmentUpdate;
  const { blockPrompt } = useBlockUnmountContext();
  const icdUpdateHook = useUpdate('ICD_UPDATED');
  const {
    medicalOrganizationService,
  } = usePatientMedicalOrganizationContext();
  const isEnrolled = patientInfo.enrolledProgramService?.isEnrolled();

  useUpdateListener('INSURANCE_ELIGIBILITY_UPDATED', () => {
    patientInfo.refetch(['patientInsuranceRefetch']);
  });

  const sendReq = async (value: EnrollmentReq<SubmitValue>) => {
    sendMixpanel({ event: MixpanelEvents.PatientEnrollmentSubmit });
    // TODO: [t] Replace hcRes and pcRes in next enhancement
    let hcRes;
    let pcRes;
    if (value.icdTable) {
      hcRes = await ApiRequestHelper.tryCatch(healthConditionUpdateInfo.send({
        params: {
          memberId: patientInfo.id,
          request: { healthConditions: value.icdTable },
        },
      }), 'Failed to update icd code');
      if (!hcRes) {
        return undefined;
      }
    }
    if (value.complexity) {
      pcRes = await ApiRequestHelper.tryCatch(patientComplexityUpdateInfo.send({
        params: {
          memberId: patientInfo.id,
          request: {
            level: value?.complexity?.level,
            source: value?.complexity?.source,
          }
        },
      }), 'Failed to update complexity');
      if (!pcRes) {
        return undefined;
      }
    }
    if (hcRes) {
      icdUpdateHook.updateValue();
    }

    const toSavePrograms = value.programs
      ? intersection(value.programs, medicalOrganizationService?.availablePrograms())
      : value.programs;

    return ApiRequestHelper.tryCatch(
      programEnrollmentUpdate.send({
        params: {
          memberId: patientInfo.id,
          request: {
            action: value.action,
            basicInformation: {
              providerId: value.provider,
              spokenLanguages: value.spokenLanguage,
              username: value.username,
              mobilePhone: value.mobilePhoneNum,
              homePhone: value.homePhoneNum,
              countryCode: (
                (value.mobilePhoneNum || value.homePhoneNum)
                  ? '1'
                  : undefined
              ),
              allowOTP: value.appOTPLogin,
              clinicId: value.clinicId,
            },
            programEnrollment: {
              patientDiagnoses: value.diagnoses,
              vitalTypes: value.vitals,
              programCategories: toSavePrograms,
              reason: value.notJoiningReason,
              hasSmartDevice: value.hasSmartPhone,
              // empty array to remove if addOnServices is not available
              addOnServices: value.addOnServices || [],
            },
          },
        },
      }),
      {
        success: '',
        error: 'Failed to update enrollment!',
        onSuccess: () => {
          setCurrValue(undefined);
        }
      }
    );
  };

  const initialValues = useMemo<ProgramEnrollmentFormComponentProps['initialValues']>(() => {
    const enrollmentData = patientInfo.enrollmentInfo?.programEnrollment;
    const enrolledPrograms = patientInfo.enrolledProgramService?.getPrograms() ?? [];
    const enrollmentVitals = enrollmentData?.vitalTypes ?? [];
    const enrollmentPrograms = enrollmentData?.programCategories ?? [];
    const enrolledVitals = patientInfo.enrolledProgramService?.getVitals() ?? [];
    return {
      diagnoses: patientInfo.enrollmentInfo?.programEnrollment?.patientDiagnoses ?? undefined,
      complexity: patientInfo?.patientComplexity,
      icdTable: patientInfo?.patientHealthCondition?.healthConditions ?? [],
      vitals: isEnrolled ? enrolledVitals : enrollmentVitals,
      programs: isEnrolled ? enrolledPrograms : enrollmentPrograms,
      clinicId: patientInfo.patientInfoService?.getClinicId() ?? undefined,
      wouldJoin: patientInfo.enrollmentInfo?.programEnrollment?.ableToJoinProgram
        ?? undefined,
      notJoiningReason: patientInfo.enrollmentInfo?.programEnrollment?.reason
        ?? undefined,
      provider: patientInfo.patientInfo?.profile?.doctorId ?? undefined,
      spokenLanguage: patientInfo.patientInfoService?.getSpokenLanguage() ?? undefined,
      username: patientInfo.patientInfo?.loginId ?? undefined,
      mobilePhoneNum: patientInfo.patientInfoService?.getMobilePhoneNumber() ?? undefined,
      homePhoneNum: patientInfo.patientInfoService?.getHomePhoneNumber() ?? undefined,
      appOTPLogin: patientInfo.patientInfoService?.getMobilePhoneOTP() ?? true,
      hasSmartPhone: patientInfo.enrollmentInfo?.programEnrollment?.hasSmartDevice ?? undefined,
      addOnServices: patientInfo.enrolledProgramService?.addOnServicesList || [],
    };
  }, [patientInfo]);

  const handleValuesChange: ProgramEnrollmentFormComponentProps['onValuesChange'] = (
    changedValues,
    allValues,
  ) => {
    setCurrValue((prev) => (
      merge(
        prev,
        allValues,
        changedValues
      )
    ));
  };

  const handleSubmit = async (value: SubmitValue) => {
    const res = await sendReq({ ...value, action: EnrollmentActionEnum.ENROLL });
    // if there was no error
    if (res !== undefined) {
      onSubmit?.(value);
    }
  };

  const handleClose = async () => {
    blockPrompt({
      next: async () => {
        if (currValue && !isEnrolled) {
          await sendReq(currValue);
        }
        onClose?.();
      }
    });
  };

  return (
    <EnrollmentWrapperComponent
      onClose={handleClose}
    >
      <ProgramEnrollmentFormComponent
        onValuesChange={handleValuesChange}
        patientId={patientInfo.id}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        patientInfo={patientInfo}
      />
    </EnrollmentWrapperComponent>
  );
};
